import { StorageEnum, UserRoleEnum } from '@/app/enum/storage.enum';

export interface JwtOrganization {
  organizationId: string;
  organizationName: string;
  role: string;
  permissions: string[];
  accountId: string;
  partner?: { id: string; [key: string]: unknown };
  branches?: { id: string; [key: string]: unknown }[];
  couriers?: { id: string; [key: string]: unknown }[];
}

export interface JwtPayload extends Record<string, unknown> {
  organizations?: JwtOrganization[];
}

export function decodeJwtPayload(token: string): JwtPayload {
  const parts = token.split('.');
  if (parts.length < 2) {
    throw new Error('Invalid token');
  }

  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const decoded = atob(base64);
  return JSON.parse(decoded) as JwtPayload;
}

export function buildUserDataFromJwt(
  payload: JwtPayload,
  org?: JwtOrganization
): Record<string, unknown> {
  const { organizations: _orgs, ...rest } = payload;
  const user: Record<string, unknown> = { ...rest };

  if (org) {
    user.role = org.role;
    user.permissions = org.permissions;
    user.organizationId = org.organizationId;
    user.organizationName = org.organizationName;
    user.partner = org.partner;
    user.branches = org.branches;
    user.couriers = org.couriers;
    user.accountId = org.accountId;
  }

  return user;
}

export function getPartnerOrderDefaults(user: Record<string, unknown>): {
  selectedOrderOption: string;
  selectedTabId: string;
} {
  const branches = (user.branches as { id: string }[]) ?? [];
  const couriers = (user.couriers as { id: string }[]) ?? [];

  if (branches.length && couriers.length) {
    return { selectedOrderOption: 'delivery', selectedTabId: couriers[0].id };
  }
  if (branches.length && !couriers.length) {
    return { selectedOrderOption: 'pickup', selectedTabId: branches[0].id };
  }
  if (!branches.length && couriers.length) {
    return { selectedOrderOption: 'delivery', selectedTabId: couriers[0].id };
  }

  return { selectedOrderOption: '', selectedTabId: '' };
}

export function getPostLoginRedirectPath(_user: Record<string, unknown>): string {
  return '/dashboard';
}

export function persistLoginSession(
  token: string,
  payload: JwtPayload,
  org: JwtOrganization | undefined,
  authId?: string
): Record<string, unknown> {
  const user = buildUserDataFromJwt(payload, org);

  localStorage.setItem(StorageEnum.userToken, token);
  document.cookie = `${StorageEnum.userToken}=${token}; path=/;`;

  if (authId) {
    localStorage.setItem(StorageEnum.fluidAuthId, authId);
  }

  localStorage.setItem(StorageEnum.jwtPayload, JSON.stringify(payload));

  if (org) {
    localStorage.setItem(StorageEnum.selectedOrganizationId, org.organizationId);
    localStorage.setItem(StorageEnum.businessId, org.accountId);
  }

  localStorage.setItem(StorageEnum.userData, JSON.stringify(user));

  if (user.role === UserRoleEnum.partner) {
    const { selectedOrderOption, selectedTabId } = getPartnerOrderDefaults(user);
    if (selectedOrderOption) {
      localStorage.setItem(StorageEnum.selectedOrderOption, selectedOrderOption);
    }
    if (selectedTabId) {
      localStorage.setItem(StorageEnum.selectedOrderTabId, selectedTabId);
    }
  } else {
    localStorage.removeItem(StorageEnum.selectedOrderOption);
    localStorage.removeItem(StorageEnum.selectedOrderTabId);
  }

  return user;
}

export function getOrganizationsFromPayload(payload: JwtPayload): JwtOrganization[] {
  return (payload.organizations ?? []) as JwtOrganization[];
}

export function getStoredJwtPayload(): JwtPayload | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(StorageEnum.jwtPayload);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as JwtPayload;
  } catch {
    return null;
  }
}
